import { BaseFieldFilter, NativeFieldFilter } from 'module/api/filter/types';
import { Operation } from 'module/api/filter/types';
import { unreachable } from 'module/helpers/unreachable';
import { EstimateField } from 'module/types/filter-field/field/filterable-field';
import {
  EstimateFieldFilter,
  EstimateFieldFilterOperationType,
  EstimateRelativeDirection,
} from 'module/types/filter-field/filter/estimate';

const toDirection = (value: string): EstimateRelativeDirection => {
  return value.includes('-')
    ? EstimateRelativeDirection.Late
    : EstimateRelativeDirection.Early;
};

const toValue = (days = 0, direction: EstimateRelativeDirection) => {
  return (direction === EstimateRelativeDirection.Late ? '-' : '') + days;
};

const toDays = (value: string): number => {
  const days = Number(value.replace('-', ''));

  return Number.isNaN(days) ? 0 : days;
};

const toDaysAndDirection = (
  value: string,
): [number, EstimateRelativeDirection] => {
  return [toDays(value), toDirection(value)];
};

const toApiFormat = (filter: EstimateFieldFilter): BaseFieldFilter => {
  switch (filter.type) {
    case EstimateFieldFilterOperationType.IsMoreThan:
      return {
        operation:
          filter.direction === EstimateRelativeDirection.Late
            ? Operation.LessThan
            : Operation.GreaterThan,
        value: toValue(filter.days, filter.direction),
        sourceType: filter.field.sourceType,
      };

    case EstimateFieldFilterOperationType.IsLessThan:
      return {
        operation:
          filter.direction === EstimateRelativeDirection.Late
            ? Operation.GreaterThan
            : Operation.LessThan,
        value: toValue(filter.days, filter.direction),
        sourceType: filter.field.sourceType,
      };

    default:
      return unreachable(filter);
  }
};

const toType = (
  operation: Operation,
  direction: EstimateRelativeDirection,
): EstimateFieldFilterOperationType => {
  switch (operation) {
    case Operation.LessThan:
      return direction === EstimateRelativeDirection.Late
        ? EstimateFieldFilterOperationType.IsMoreThan
        : EstimateFieldFilterOperationType.IsLessThan;
    case Operation.GreaterThan:
      return direction === EstimateRelativeDirection.Late
        ? EstimateFieldFilterOperationType.IsLessThan
        : EstimateFieldFilterOperationType.IsMoreThan;
    case Operation.HasValue:
    case Operation.IsNotEmpty:
    case Operation.IsEmpty:
    case Operation.IsNull:
    case Operation.Value:
    case Operation.IsNot:
    case Operation.OneOf:
    case Operation.Contains:
    case Operation.NoneOf:
    default:
      return unreachable(operation as never);
  }
};

const parse = (
  filter: NativeFieldFilter,
  field: EstimateField,
): EstimateFieldFilter => {
  const value = (filter.value ?? '') as string;
  const [days, direction] = toDaysAndDirection(value);
  const type = toType(filter.operation, direction);

  switch (type) {
    case EstimateFieldFilterOperationType.IsMoreThan:
    case EstimateFieldFilterOperationType.IsLessThan: {
      return {
        field,
        type,
        days,
        direction,
      };
    }
    default:
      return unreachable(type);
  }
};

export const EstimateFilter = {
  toApiFormat,
  parse,
};
